import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { fetchMessages, sendMessage, markMessagesRead, groupThreads } from '../../lib/messages';
import { s } from '../../lib/workerStyles';

export default function MessagesScreen({ onBack }) {
  const { profile } = useAuth();
  const [threads, setThreads] = useState([]);
  const [managers, setManagers] = useState([]);
  const [recipientId, setRecipientId] = useState('');
  const [body, setBody] = useState('');
  const [activeThread, setActiveThread] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isWorker = profile?.role === 'worker';

  useEffect(() => {
    load();

    // load manager/supervisor list for the compose picker — profiles' own RLS
    // restricts a worker to their own row, so this goes through a security
    // definer function scoped to just id/full_name/role instead of querying
    // the table directly (see list_staff_contacts() in demo-fixes.sql).
    supabase
      .rpc('list_staff_contacts')
      .then(({ data }) => {
        setManagers(data || []);
        if (data && data.length && !recipientId) setRecipientId(data[0].id);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      const msgs = await fetchMessages();
      const grouped = groupThreads(msgs, profile.id);
      setThreads(grouped);
    } catch (e) {
      setError(e.message);
    }
  }

  async function openThread(t) {
    setActiveThread(t);
    // mark unread as read, in one batched request instead of one per message
    const unreadIds = t.messages.filter(m => !m.read && m.recipient_id === profile.id).map(m => m.id);
    if (unreadIds.length > 0) {
      try { await markMessagesRead(unreadIds); } catch (_) {}
    }
    await load();
  }

  async function handleSend() {
    if (!recipientId || !body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await sendMessage({
        senderId: profile.id,
        senderRole: profile.role,
        recipientId,
        body,
      });
      setBody('');
      await load();
      const t = threads.find(x => x.otherId === recipientId);
      if (t) setActiveThread(t);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }

  function fmt(ts) {
    return new Date(ts).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  function otherName(id) {
    const m = managers.find(x => x.id === id);
    return m ? m.full_name : (isWorker ? 'Manager' : 'Unknown');
  }

  return (
    <div style={s.screen}>
      <div style={s.navBar}>
        <button style={s.backBtn} onClick={onBack}>‹</button>
        <div style={s.navTitle}>Messages</div>
        <div style={{ width: 40 }} />
      </div>

      {error && <div style={s.err}>{error}</div>}

      {activeThread ? (
        <div style={{ padding: 12 }}>
          <button style={s.linkBtn} onClick={() => setActiveThread(null)}>← All messages</button>
          <div style={s.threadHead}>{otherName(activeThread.otherId)}</div>
          <div style={s.thread}>
            {activeThread.messages.slice().reverse().map(m => {
              const mine = m.sender_id === profile.id;
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                  <div style={{
                    maxWidth: '80%', padding: '8px 12px', borderRadius: 12,
                    background: mine ? '#1d4ed8' : '#1e293b',
                    color: '#f1f5f9', fontSize: 14, lineHeight: 1.4,
                  }}>
                    {m.body}
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, textAlign: 'right' }}>{fmt(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div style={{ padding: 12 }}>
          {threads.length === 0 && <div style={s.empty}>No messages yet.</div>}
          {threads.map(t => (
            <button key={t.otherId} style={s.threadRow} onClick={() => openThread(t)}>
              <div style={s.threadRowName}>
                {otherName(t.otherId)}
                {t.unread > 0 && <span style={s.badge}>{t.unread}</span>}
              </div>
              <div style={s.threadRowPreview}>
                {(t.messages[t.messages.length - 1]?.body || '').slice(0, 60)}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Compose (always visible at bottom) */}
      <div style={s.compose}>
        <div style={s.composeLabel}>
          {isWorker ? 'To: ' : 'To: '}
          <select style={s.select} value={recipientId} onChange={e => setRecipientId(e.target.value)}>
            {managers.map(m => <option key={m.id} value={m.id}>{m.full_name} ({m.role})</option>)}
          </select>
        </div>
        <textarea style={s.textarea} rows={2} placeholder="Type a message..." value={body}
          onChange={e => setBody(e.target.value)} />
        <button style={busy || !body.trim() ? { ...s.sendBtn, opacity: 0.6 } : s.sendBtn}
          disabled={busy || !body.trim()} onClick={handleSend}>
          {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
